import React from 'react';
import "./upper-extremity-prosthetics.css";
import { Link } from "react-router-dom";

function UpperExtremityProsthetics() {
  const prostheticTypes = [
    {
      id: "transradial",
      title: "Transradial Prosthetics",
      description: "Custom-designed devices for below-elbow amputations, offering precision control and natural movement.",
      features: ["Myoelectric control", "Body-powered options", "Activity-specific terminals", "Custom sockets"]
    },
    {
      id: "transhumeral",
      title: "Transhumeral Prosthetics",
      description: "Advanced solutions for above-elbow amputations with sophisticated elbow mechanisms.",
      features: ["Electronic elbows", "Harnessed systems", "Hybrid designs", "Multi-articulating hands"]
    },
    {
      id: "shoulder-disarticulation",
      title: "Shoulder Disarticulation",
      description: "Specialized prosthetics for shoulder-level amputations, maximizing function with minimal weight.",
      features: ["Lightweight materials", "Extended reach", "Custom framing", "Passive options"]
    },
    {
      id: "partial-hand",
      title: "Partial Hand Solutions",
      description: "Innovative devices for partial hand amputations, preserving sensation and dexterity.",
      features: ["Digit replacements", "Opposition posts", "Custom silicone", "Functional aesthetics"]
    }
  ];

  const technologies = [
    {
      id: "myoelectric",
      title: "Myoelectric Control",
      description: "Advanced systems that use muscle signals to control prosthetic movements naturally and intuitively."
    },
    {
      id: "bionic-hands",
      title: "Bionic Hands",
      description: "Multi-articulating hands with individual finger movement for precise grip patterns."
    },
    {
      id: "pattern-recognition",
      title: "Pattern Recognition",
      description: "AI-powered systems that learn and predict your movement intentions for smoother control."
    },
    {
      id: "osseointegration",
      title: "Osseointegration",
      description: "Direct bone attachment for improved stability, comfort, and sensory feedback."
    }
  ];

  const benefits = [
    {
      id: "function",
      title: "Restored Function",
      description: "Perform daily tasks with greater ease and independence using advanced prosthetic technology."
    },
    {
      id: "comfort",
      title: "Superior Comfort",
      description: "Custom-fitted sockets and lightweight materials ensure all-day wearability."
    },
    {
      id: "aesthetics",
      title: "Natural Appearance",
      description: "Lifelike silicone covers and custom coloring for a natural look."
    },
    {
      id: "durability",
      title: "Long-Lasting Performance",
      description: "High-quality components designed for reliability in daily use."
    }
  ];

  const processSteps = [
    {
      id: "step1",
      number: "01",
      title: "Comprehensive Evaluation",
      description: "Thorough assessment of your needs, lifestyle, and physical capabilities."
    },
    {
      id: "step2",
      number: "02",
      title: "Precision Fitting",
      description: "Advanced scanning and casting for a perfectly fitting socket."
    },
    {
      id: "step3",
      number: "03",
      title: "Component Selection",
      description: "Expert guidance in choosing the right hand, wrist, and elbow components."
    },
    {
      id: "step4",
      number: "04",
      title: "Training & Adaptation",
      description: "Comprehensive occupational therapy to master your new device."
    }
  ];

  const terminalDevices = [
    {
      id: "td1",
      name: "Multi-articulating Hands",
      description: "Individual finger movement for natural grip patterns"
    },
    {
      id: "td2",
      name: "Hook Terminal Devices",
      description: "Durable, lightweight options for heavy-duty tasks"
    },
    {
      id: "td3",
      name: "Activity-Specific Tools",
      description: "Specialized attachments for sports, work, and hobbies"
    },
    {
      id: "td4",
      name: "Passive Hands",
      description: "Lifelike appearance with functional positioning"
    },
    {
      id: "td5",
      name: "Electric Grippers",
      description: "Powered options for strong, reliable grip"
    },
    {
      id: "td6",
      name: "Cosmetic Gloves",
      description: "Custom-colored silicone for natural appearance"
    }
  ];

  const faqs = [
    {
      question: "How long does it take to get an upper extremity prosthetic?",
      answer: "The process typically takes 4-8 weeks from initial consultation to delivery, depending on the complexity of the device and component availability."
    },
    {
      question: "Will I be able to perform all daily activities?",
      answer: "While prosthetics can restore many functions, the level of function depends on your amputation level, chosen components, and training. Our team works with you to set realistic goals and maximize your independence."
    },
    {
      question: "What is the difference between myoelectric and body-powered?",
      answer: "Myoelectric prosthetics use muscle signals to control electric motors for natural movement. Body-powered devices use cables and harnesses for durable, reliable function. Many patients benefit from having both options."
    },
    {
      question: "Does insurance cover upper extremity prosthetics?",
      answer: "Most insurance plans provide coverage for medically necessary upper limb prosthetics. Our team will verify your benefits and assist with the approval process."
    }
  ];

  return (
    <div className="upper-extremity-page">

      {/* Hero Section */}
      <section className="ue-hero-section">
        <div className="ue-hero-overlay">
          <div className="ue-container">
            <div className="ue-hero-content">
              <span className="ue-hero-badge">Upper Extremity Prosthetics</span>
              <h1 className="ue-hero-title">
                Advanced Solutions for <span className="ue-hero-highlight">Upper Limb Mobility</span>
              </h1>
              <p className="ue-hero-subtitle">
                Restore function, independence, and confidence with our state-of-the-art upper limb 
                prosthetic solutions, customized to your unique needs and lifestyle.
              </p>
              <div className="ue-hero-buttons">
                <Link to="/contactus" className="ue-btn ue-btn-primary">Schedule Consultation</Link>
               
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="ue-intro-section">
        <div className="ue-container">
          <div className="ue-intro-grid">
            <div className="ue-intro-content">
              <span className="ue-section-badge">Overview</span>
              <h2 className="ue-intro-heading">Reaching New Possibilities</h2>
              <p className="ue-intro-text">
                At Southern Rehab Care, we understand that your upper limbs are essential to nearly 
                every aspect of daily life. Our upper extremity prosthetic solutions are designed 
                to restore not just function, but the ability to express yourself, connect with others, 
                and pursue your passions.
              </p>
              <p className="ue-intro-text">
                From simple passive devices to advanced bionic hands with individual finger movement, 
                we offer a full spectrum of technologies. Our team works closely with you to understand 
                your goals and select the solution that best fits your needs, abilities, and lifestyle.
              </p>
              <div className="ue-intro-stats">
                <div className="ue-intro-stat">
                  <span className="ue-intro-stat-number">300+</span>
                  <span className="ue-intro-stat-label">Patients Fitted</span>
                </div>
                <div className="ue-intro-stat">
                  <span className="ue-intro-stat-number">100%</span>
                  <span className="ue-intro-stat-label">Satisfaction Rate</span>
                </div>
                <div className="ue-intro-stat">
                  <span className="ue-intro-stat-number">15+</span>
                  <span className="ue-intro-stat-label">Years Experience</span>
                </div>
              </div>
            </div>
            <div className="ue-intro-image-wrapper">
              <img src="/upper-extremity-prosthetics.webp" alt="Upper Extremity Prosthetic" className="ue-intro-image" />
            </div>
          </div>
        </div>
      </section>

      {/* Prosthetic Types Section */}
      <section className="ue-types-section">
        <div className="ue-container">
          <div className="ue-section-header">
            <span className="ue-section-badge">Our Solutions</span>
            <h2 className="ue-section-heading">Types of Upper Extremity Prosthetics</h2>
            <p className="ue-section-subheading">Customized solutions for every level of amputation</p>
          </div>


          <div className='lower-Extremity-images'>
            <img src='/upper1.jpg' className='image-lower-extremity' />
            <img src='/upper2.jpg' className='image-lower-extremity'/>
            <img src='/upper3.jpg' className='image-lower-extremity'/>
            <img src='/upper8.jpg' className='image-lower-extremity'/>
          </div>


          <div className="ue-types-grid">
            {prostheticTypes.map((type) => (
              <div key={type.id} className="ue-type-card">
                <h3 className="ue-type-title">{type.title}</h3>
                <p className="ue-type-description">{type.description}</p>
                <ul className="ue-type-features">
                  {type.features.map((feature, index) => (
                    <li key={index} className="ue-type-feature">
                      <span className="ue-type-feature-dot"></span>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>




      {/* Technologies Section */}
      <section className="ue-tech-section">
        <div className="ue-container">
          <div className="ue-section-header">
            <span className="ue-section-badge ue-section-badge-light">Advanced Technology</span>
            <h2 className="ue-section-heading ue-section-heading-light">Cutting-Edge Solutions</h2>
            <p className="ue-section-subheading ue-section-subheading-light">Innovation that puts control in your hands</p>
          </div>
          <div className="ue-tech-grid">
            {technologies.map((tech) => (
              <div key={tech.id} className="ue-tech-card">
                <h3 className="ue-tech-title">{tech.title}</h3>
                <p className="ue-tech-description">{tech.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Terminal Devices Section */}
      <section className="ue-terminal-section">
        <div className="ue-container">
          <div className="ue-terminal-grid">
            <div className="ue-terminal-content">
              <span className="ue-section-badge">Terminal Devices</span>
              <h2 className="ue-terminal-heading">Choose Your Perfect Hand</h2>
              <p className="ue-terminal-text">
                From bionic hands with individual finger movement to durable hooks for heavy-duty tasks, 
                we offer a wide selection of terminal devices to match your specific needs and activities.
              </p>
            </div>
            <div className="ue-terminal-list">
              {terminalDevices.map((device) => (
                <div key={device.id} className="ue-terminal-item">
                  <h3 className="ue-terminal-item-title">{device.name}</h3>
                  <p className="ue-terminal-item-description">{device.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="ue-benefits-section">
        <div className="ue-container">
          <div className="ue-section-header">
            <span className="ue-section-badge">Why Choose Us</span>
            <h2 className="ue-section-heading">Benefits of Our Upper Extremity Prosthetics</h2>
            <p className="ue-section-subheading">Engineered for your active lifestyle</p>
          </div>
          <div className="ue-benefits-grid">
            {benefits.map((benefit) => (
              <div key={benefit.id} className="ue-benefit-card">
                <h3 className="ue-benefit-title">{benefit.title}</h3>
                <p className="ue-benefit-description">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="ue-process-section">
        <div className="ue-container">
          <div className="ue-section-header">
            <span className="ue-section-badge">Our Process</span>
            <h2 className="ue-section-heading">Your Journey to Independence</h2>
            <p className="ue-section-subheading">A step-by-step approach to your new prosthetic</p>
          </div>
          <div className="ue-process-grid">
            {processSteps.map((step) => (
              <div key={step.id} className="ue-process-card">
                <div className="ue-process-number">{step.number}</div>
                <h3 className="ue-process-title">{step.title}</h3>
                <p className="ue-process-description">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="ue-faq-section">
        <div className="ue-container">
          <div className="ue-section-header">
            <span className="ue-section-badge">FAQ</span>
            <h2 className="ue-section-heading">Frequently Asked Questions</h2>
            <p className="ue-section-subheading">Common questions about upper extremity prosthetics</p>
          </div>
          <div className="ue-faq-grid">
            {faqs.map((faq, index) => (
              <div key={index} className="ue-faq-item">
                <h3 className="ue-faq-question">{faq.question}</h3>
                <p className="ue-faq-answer">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="ue-cta-section">
        <div className="ue-container">
          <div className="ue-cta-content">
            <h2 className="ue-cta-heading">Ready to Take the Next Step?</h2>
            <p className="ue-cta-subheading">Schedule a consultation with our upper extremity specialists</p>
            <div className="ue-cta-buttons">
              <Link to="/contactus" className="ue-btn ue-btn-primary">Contact Us</Link>
              <Link to="/contactus" className="ue-btn ue-btn-secondary-light">Book Appointment</Link>
            </div>
          </div>
        </div>
      </section>

    </div>
  );
}

export default UpperExtremityProsthetics;